/**
 * MTO List – Slider Web Component
 *
 * Handles grid ↔ slider mode switching based on viewport,
 * autoplay, arrow navigation, and pause-on-hover.
 */
if (!customElements.get('mto-list-slider')) {
  class MtoListSlider extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._autoplayTimer = null;
      this._resizeTimer = null;

      // Bind handlers so they can be added/removed cleanly
      this._onResize = this._onResize.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
    }

    connectedCallback() {
      // Wait for DOM if needed (theme editor can be slow)
      this._waitForDOM();
    }

    disconnectedCallback() {
      this._cleanup();
    }

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    _waitForDOM(attempts = 0) {
      if (this._initialized) return;

      const track = this.querySelector('.mto-list__track');
      if (track) {
        this._setup(track);
      } else if (attempts < 20) {
        setTimeout(() => this._waitForDOM(attempts + 1), 50);
      }
    }

    _setup(track) {
      this.track = track;
      this.prevBtn = this.querySelector('.mto-list__arrow--prev');
      this.nextBtn = this.querySelector('.mto-list__arrow--next');

      this.config = {
        desktopSlider: this.dataset.sliderDesktop === 'true',
        mobileSlider: this.dataset.sliderMobile === 'true',
        autoplay: this.dataset.autoplay === 'true',
        speed: parseInt(this.dataset.autoplaySpeed, 10) || 4000,
        pauseHover: this.dataset.pauseHover === 'true',
      };

      // Attach persistent listeners
      window.addEventListener('resize', this._onResize);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

      if (this.config.pauseHover) {
        this.addEventListener('mouseenter', this._onMouseEnter);
        this.addEventListener('mouseleave', this._onMouseLeave);
      }

      this._initialized = true;
      this._applyMode();
    }

    _cleanup() {
      window.removeEventListener('resize', this._onResize);
      this.track?.removeEventListener('scroll', this._onScroll);

      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);

      this.removeEventListener('mouseenter', this._onMouseEnter);
      this.removeEventListener('mouseleave', this._onMouseLeave);

      this._stopAutoplay();
      clearTimeout(this._resizeTimer);
      this._initialized = false;
    }

    // ------------------------------------------------------------------
    // Mode switching (grid ↔ slider)
    // ------------------------------------------------------------------

    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._applyMode(), 100);
    }

    _applyMode() {
      const isMobile = window.innerWidth <= 749;
      const shouldSlide =
        (isMobile && this.config.mobileSlider) ||
        (!isMobile && this.config.desktopSlider);

      if (shouldSlide) {
        this._enableSlider();
      } else {
        this._disableSlider();
      }
    }

    _enableSlider() {
      this.track.classList.add('is-slider');
      this._showArrows(true);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._updateArrowState();

      if (this.config.autoplay) this._startAutoplay();
    }

    _disableSlider() {
      this.track.classList.remove('is-slider');
      this.track.removeEventListener('scroll', this._onScroll);
      this.track.scrollLeft = 0;
      this._showArrows(false);
      this._stopAutoplay();
    }

    // ------------------------------------------------------------------
    // Arrow navigation
    // ------------------------------------------------------------------

    _showArrows(visible) {
      if (this.prevBtn) this.prevBtn.hidden = !visible;
      if (this.nextBtn) this.nextBtn.hidden = !visible;
    }

    _onPrev() {
      this._scrollBy('left');
      this._resetAutoplay();
    }

    _onNext() {
      this._scrollBy('right');
      this._resetAutoplay();
    }

    _scrollBy(direction) {
      const item = this.track.querySelector('.mto-list__item');
      if (!item) return;

      const scrollAmount =
        item.offsetWidth + (parseInt(getComputedStyle(this.track).gap, 10) || 0);

      if (direction === 'left') {
        this.track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        // Loop back to start when reaching the end
        if (Math.ceil(this.track.scrollLeft) >= maxScroll - 5) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }
    }

    _onScroll() {
      // Debounce arrow state updates (scroll fires rapidly)
      cancelAnimationFrame(this._scrollRAF);
      this._scrollRAF = requestAnimationFrame(() => this._updateArrowState());
    }

    _updateArrowState() {
      if (!this.prevBtn || !this.nextBtn) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      // If all items fit (no overflow), disable both
      if (maxScroll <= 0) {
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
        return;
      }

      this.prevBtn.disabled = this.track.scrollLeft <= 5;
      // Don't disable next when autoplay loops
      this.nextBtn.disabled =
        !this.config.autoplay && this.track.scrollLeft >= maxScroll - 5;
    }

    // ------------------------------------------------------------------
    // Autoplay
    // ------------------------------------------------------------------

    _startAutoplay() {
      this._stopAutoplay();
      this._autoplayTimer = setInterval(
        () => this._scrollBy('right'),
        this.config.speed
      );
    }

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    _resetAutoplay() {
      if (this.config.autoplay) {
        this._startAutoplay();
      }
    }

    _onMouseEnter() {
      this._stopAutoplay();
    }

    _onMouseLeave() {
      if (this.config.autoplay) this._startAutoplay();
    }
  }

  customElements.define('mto-list-slider', MtoListSlider);
}
